/**
 * Plugin Loader
 * 
 * Loads and initializes frontend plugins based on backend plugin list.
 * Registers plugin pages, navigation, etc. with frontend registries.
 *
 * IMPORTANT: Plugin UI sources may be discovered from either:
 *   1. neuro-core/plugins/*      (historical symlink or container mount)
 *   2. repo-root/plugins/*       (direct monorepo path in local dev)
 *
 * Vite's import.meta.glob does NOT resolve aliases (@plugins) — it only
 * works with filesystem-relative paths.  The glob patterns below use
 * literal filesystem-relative paths from this file's location:
 *
 *   frontend/src/plugins/PluginLoader.tsx
 *     ../../../plugins  → neuro-core/plugins
 *     ../../../../plugins → repo-root/plugins
 *
 * NOTE: If a new plugin is added to the plugins/ directory, the Vite dev
 * server must be restarted for import.meta.glob to discover the new files.
 *
 * The Vite config must have server.fs.allow entries that permit serving
 * files from outside the project root (see vite.config.ts).
 */

import React, { useEffect, useState, useRef, lazy } from 'react';
import { listPlugins } from '../api/plugins';
import client from '../api/client';
import { usePageRegistry } from './registries/page-registry';
import type { PluginPageDefinition } from './registries/page-registry';
import { useNavigationRegistry } from './registries/navigation-registry';
import { useActionRegistryStore } from './registries/action-registry';
import { useEntityTypeRegistry } from './registries/entity-type-registry';
import { useSearchResultTypeRegistry } from './registries/search-result-type-registry';
import { usePageActionRegistry } from './registries/page-action-registry';
import { useDashboardRegistry } from './registries/dashboard-registry';
import { DashboardPluginWidget } from './extension-points/DashboardPluginWidget';
import { useSidebarRegistry } from './registries/sidebar-registry';
import { useOverlayCommandRegistry } from '../ui/commands/command-registry';
import { registerCoreCommands } from '../ui/commands/core-commands';
// Phase 3.4: Additional registry imports for hot-reload cleanup
import { useCommandRegistry } from './registries/command-registry';
import { useWidgetRegistry } from './registries/widget-registry';
import { useMenuRegistry } from './registries/menu-registry';
import { useThemeRegistry } from './registries/theme-registry';
import { useChatOptionsRegistry } from './registries/chat-options-registry';
import { useChatMessageExtensionRegistry } from './registries/chat-message-extension-registry';
import { usePluginEventBusStore } from './plugin-event-bus';
// Phase 3.5: Deferred action cleanup
import { useDeferredActionStore } from './deferred-action-registry';
import { createPluginUIExtensions, executePluginTeardown } from './sdk/plugin-ui-extensions';
import { pluginToastBridge } from './toast-bridge';
import { actionResultBridge } from './action-result-bridge';
import { pluginModalBridge } from './modal-bridge';
import type { PluginModalRequest } from './modal-bridge';
import { useNotifications } from '../hooks/useNotifications';
import type { NavigationItem, NavigationCategory } from '../api/platform';

// ── Wave 22.13.9: Plugin Sandbox & Error Boundaries ────────────
import { PluginErrorBoundary } from './sandbox/plugin-error-boundary';
import { handlePluginCrash, registerTeardownHandler, isPluginDisabledByCrash } from './sandbox/plugin-crash-recovery';
import { usePluginLogStore } from './sandbox/plugin-log-store';

// ── Wave 22.13.1: Host UI Integration ───────────────────────────
import { registerModalComponent } from '../components/PluginModalHost';

// ── Wave 22.13.2: Runtime Verification Tracking ─────────────────
import {
  recordPluginSetupVerification,
} from './interaction-warnings';

// ── Design System integration ───────────────────────────────────
import {
  ThemeProvider,
  ToastProvider,
  ComponentRegistryProvider,
  initializeRegistry,
  componentRegistry,
} from '@neuro/design-system';
import '@neuro/design-system/styles.css';

// ── Device detection provider ───────────────────────────────────
import { DeviceTypeProvider } from '../providers/DeviceTypeProvider';

// ── UI Slot System ──────────────────────────────────────────────
// Import from the /ui sub-path to avoid pulling in NestJS/Node code
// that is part of the main SDK barrel.
import { slotRegistry } from '@neuro/plugin-sdk/ui';
import type { SlotContribution } from '@neuro/plugin-sdk/ui';
import type {
  ManifestSlotDefinition,
  ManifestSlotContribution,
} from '@neuro/plugin-sdk/types';

// Core-owned slots any plugin may fill. No owner plugin, so contributors need no
// manifest `dependencies` entry (a plugin-owned slot would make them hard-depend on it).
//   entity.detail — panels on an item's detail view. Rendered by todos, notes,
//   projects and thoughts with { entityType, entityId, title?, ownerId?, isOwner?, navigate? };
//   SourceLink fills it with the item's "Saved sources".
slotRegistry.defineSlot({
  id: 'entity.detail',
  description: 'Panels on an entity detail view. Context: { entityType: "todo" | "note" | "project" | "thought", entityId, title?, ownerId?, isOwner?, navigate? }',
  multiple: true,
});

// Initialize the shared component registry at module load time
// so all design-system components are available before any plugin loads.
initializeRegistry();

// Register core-owned pages (pages that live in src/pages/ rather than in a
// plugin).  Run at module load time so they are available before any plugin
// UI is processed.  Plugins may override these by registering the same path.
import { registerCorePages } from './core-pages';
registerCorePages();

// Embeddable plugin components (rendered with their owning plugin's context
// by PluginEmbedHost; consumed via <EmbeddedPluginComponent> from the SDK).
import { installEmbedHost, registerPluginEmbeddables, unregisterPluginEmbeddables } from './embed/install-embed-host';
import { resolveComponentLoader } from './embed/resolve-component-loader';
installEmbedHost();

// ── Vite glob discovery ──────────────────────────────────────────
// CRITICAL: Use relative filesystem paths, NOT aliases.
// Aliases (@plugins) do NOT work with import.meta.glob.
//
// The glob discovers ALL plugins; the filter below runs at runtime
// and selects only what the current build profile includes.

// eslint-disable-next-line prefer-const
declare const __NEURO_PLUGIN_INCLUDE__: string[] | undefined;
const PLUGIN_INCLUDE_LIST: string[] | undefined =
  typeof __NEURO_PLUGIN_INCLUDE__ !== 'undefined' ? __NEURO_PLUGIN_INCLUDE__ : undefined;
const isDesktopMode = PLUGIN_INCLUDE_LIST !== undefined;

function isPluginIncluded(key: string): boolean {
  if (!PLUGIN_INCLUDE_LIST) return true;
  const match = key.match(/plugins[/\\]([^/\\]+)[/\\]/);
  return match ? PLUGIN_INCLUDE_LIST.includes(match[1]) : false;
}

function filterPluginMap<T>(mods: Record<string, T>): Record<string, T> {
  if (!isDesktopMode) return mods;
  const filtered: Record<string, T> = {};
  for (const key of Object.keys(mods)) {
    if (isPluginIncluded(key)) filtered[key] = mods[key];
  }
  if (Object.keys(filtered).length === 0 && PLUGIN_INCLUDE_LIST) {
    console.warn(
      `[PluginLoader] Desktop mode — no plugins found include list: ` +
      PLUGIN_INCLUDE_LIST.join(', ')
    );
  } else {
    console.log(
      `[PluginLoader] Desktop mode — ${Object.keys(filtered).length} of ` +
      `${Object.keys(mods).length} plugin modules included ` +
      `(${PLUGIN_INCLUDE_LIST!.join(', ')})`
    );
  }
  return filtered;
}

const pluginModules = filterPluginMap(import.meta.glob<Record<string, any>>(
  [
    '../../../plugins/*/ui/index.ts',
    '../../../../plugins/*/ui/index.ts',
    '../../../../desktop/staging/build-plugins/*/ui/index.ts',
  ],
  { eager: false },
));

const pluginPages = filterPluginMap(import.meta.glob<Record<string, any>>(
  [
    '../../../plugins/*/ui/pages/*.tsx',
    '../../../../plugins/*/ui/pages/*.tsx',
    '../../../../desktop/staging/build-plugins/*/ui/pages/*.tsx',
  ],
  { eager: false },
));

const pluginComponents = filterPluginMap(import.meta.glob<Record<string, any>>(
  [
    '../../../plugins/*/ui/components/*.tsx',
    '../../../../plugins/*/ui/components/*.tsx',
    '../../../../desktop/staging/build-plugins/*/ui/components/*.tsx',
  ],
  { eager: false },
));

const pluginManifests = filterPluginMap(import.meta.glob<Record<string, any>>(
  [
    '../../../plugins/*/plugin.manifest.json',
    '../../../../plugins/*/plugin.manifest.json',
    '../../../../desktop/staging/build-plugins/*/plugin.manifest.json',
  ],
  { eager: true },
));

// ── Debug flag ──────────────────────────────────────────────────
// Enable verbose plugin loader diagnostics via localStorage:
//   localStorage.setItem('pluginLoader.debug', '1')
const PLUGIN_DEBUG =
  typeof localStorage !== 'undefined' && localStorage.getItem('pluginLoader.debug') === '1';

function debugLog(...args: any[]) {
  if (PLUGIN_DEBUG) console.log(...args);
}

// ── DIAGNOSTIC: Vite Glob Discovery ─────────────────────────────
debugLog('PLUGIN_UI_INDEX_KEYS', Object.keys(pluginModules));
debugLog('PLUGIN_UI_PAGE_KEYS', Object.keys(pluginPages));
debugLog('PLUGIN_UI_COMPONENT_KEYS', Object.keys(pluginComponents));

// ── Empty-glob detection ─────────────────────────────────────────
// If import.meta.glob found zero plugin modules, the most common causes are:
//   1. The neuro-core/plugins symlink was missing when the Vite dev server started.
//   2. The Vite dev server needs to be restarted after the symlink was (re)created.
//   3. In Docker: the plugins directory was empty at build time.
// Skipped in desktop mode, where filterPluginMap already warns.
if (!isDesktopMode && Object.keys(pluginModules).length === 0) {
  console.warn(
    '[PluginLoader] ⚠ import.meta.glob discovered ZERO plugin UI modules. ' +
    'Checked both neuro-core/plugins and repo-root/plugins. If plugin files were ' +
    'moved or a symlink was recreated, restart the Vite dev server.',
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3.4: Clean ALL frontend registries during hot-reload
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Unregister all UI registrations for a plugin from all frontend registries.
 * Called before re-loading a plugin's UI module during hot-reload to ensure
 * no stale registrations remain.
 */
function cleanupPluginRegistrations(pluginId: string): void {
  debugLog(`[PluginLoader] 🧹 Cleaning up registrations for plugin "${pluginId}"`);

  // Execute any plugin-registered teardown handlers
  executePluginTeardown(pluginId);

  // Clean event bus subscriptions
  usePluginEventBusStore.getState().offAll(pluginId);

  // Clean all registries that have per-plugin unregister methods
  try { usePageRegistry.getState().unregisterPluginPages(pluginId); } catch (e) { debugLog('  - pages cleanup error:', e); }
  try { useActionRegistryStore.getState().unregisterPlugin(pluginId); } catch (e) { debugLog('  - actions cleanup error:', e); }
  try { useEntityTypeRegistry.getState().unregisterPlugin(pluginId); } catch (e) { debugLog('  - entityTypes cleanup error:', e); }
  try { useSearchResultTypeRegistry.getState().unregisterPlugin(pluginId); } catch (e) { debugLog('  - searchResultTypes cleanup error:', e); }
  try { usePageActionRegistry.getState().unregisterPlugin(pluginId); } catch (e) { debugLog('  - pageActions cleanup error:', e); }
  try { useSidebarRegistry.getState().unregisterPluginItems(pluginId); } catch (e) { debugLog('  - sidebar cleanup error:', e); }
  try { useCommandRegistry.getState().unregisterPluginCommands(pluginId); } catch (e) { debugLog('  - commands cleanup error:', e); }
  try { useWidgetRegistry.getState().unregisterPluginWidgets(pluginId); } catch (e) { debugLog('  - widgets cleanup error:', e); }
  try { useMenuRegistry.getState().unregisterPluginItems(pluginId); } catch (e) { debugLog('  - menu cleanup error:', e); }
  try { useThemeRegistry.getState().unregisterPluginThemes(pluginId); } catch (e) { debugLog('  - themes cleanup error:', e); }
  try { useChatOptionsRegistry.getState().unregisterPluginOptions(pluginId); } catch (e) { debugLog('  - chatOptions cleanup error:', e); }
  try { useChatMessageExtensionRegistry.getState().unregisterPluginExtensions(pluginId); } catch (e) { debugLog('  - chatMessageExtensions cleanup error:', e); }

  // Navigation registry: no per-plugin method, so we rebuild from scratch
  // (managed by backend sync)
  
  // Dashboard widgets are keyed `${pluginId}:${widgetId}`; drop this plugin's
  // from the store and the shared map the Dashboard page reads, so a disabled
  // plugin doesn't leave a stale tile behind.
  try {
    const prefix = `${pluginId}:`;
    const dashboardStore = useDashboardRegistry.getState();
    for (const panelId of Array.from(dashboardStore.panelComponents.keys())) {
      if (panelId.startsWith(prefix)) dashboardStore.unregisterComponent(panelId);
    }
    const shared = (globalThis as typeof globalThis & {
      __NEURO_DASHBOARD_PANEL_REGISTRY__?: { components: Map<string, unknown>; definitions: Map<string, unknown> };
    }).__NEURO_DASHBOARD_PANEL_REGISTRY__;
    for (const map of [shared?.components, shared?.definitions]) {
      for (const panelId of Array.from(map?.keys() ?? [])) {
        if (panelId.startsWith(prefix)) map!.delete(panelId);
      }
    }
  } catch (e) { debugLog('  - dashboard cleanup error:', e); }

  // Slot registry: unregister plugin contributions
  try { slotRegistry.unloadPlugin(pluginId); } catch (e) { debugLog('  - slots cleanup error:', e); }

  try { unregisterPluginEmbeddables(pluginId); } catch (e) { debugLog('  - embeddables cleanup error:', e); }

  // Phase 3.5: Unregister deferred actions (and reject any pending ones)
  try { useDeferredActionStore.getState().unregisterPlugin(pluginId); } catch (e) { debugLog('  - deferredActions cleanup error:', e); }

  debugLog(`[PluginLoader] 🧹 Cleanup complete for plugin "${pluginId}"`);
}

/**
 * Extract a pluginId from a glob key.
 *
 * Handles both relative-path keys and alias keys:
 *   "../../../plugins/myplugin/ui/index.ts"   → "myplugin"
 *   "@plugins/myplugin/ui/index.ts"            → "myplugin"
 *   "/abs/path/plugins/myplugin/ui/index.ts"   → "myplugin"
 *
 * The strategy: find the "plugins/" segment and take the next path component.
 */
function extractPluginId(globKey: string): string {
  const match = globKey.match(/plugins\/([^/]+)\//);
  const pluginId = match?.[1] ?? '';
  // ── DIAGNOSTIC: Plugin ID Extraction ──────────────────────────
  debugLog('PLUGIN_ID_EXTRACTED', pluginId, 'FROM', globKey);
  return pluginId;
}

/**
 * Build lookup maps keyed by pluginId for O(1) access.
 */
function buildModuleLookup() {
  const modules: Record<string, () => Promise<any>> = {};
  for (const [key, loader] of Object.entries(pluginModules)) {
    const id = extractPluginId(key);
    if (id) modules[id] = loader;
  }
  return modules;
}

function buildPageLookup() {
  // Map: pluginId → { ComponentName → loader }
  const pages: Record<string, Record<string, () => Promise<any>>> = {};

  // Discover pages from ui/pages/*.tsx
  for (const [key, loader] of Object.entries(pluginPages)) {
    const pluginId = extractPluginId(key);
    if (!pluginId) continue;
    const fileName = key.split('/').pop()?.replace(/\.tsx?$/, '') ?? '';
    if (!pages[pluginId]) pages[pluginId] = {};
    pages[pluginId][fileName] = loader;
  }

  // Also discover components from ui/components/*.tsx so that page
    // definitions referencing components (e.g. PluginEditor) resolve.
  for (const [key, loader] of Object.entries(pluginComponents)) {
    const pluginId = extractPluginId(key);
    if (!pluginId) continue;
    const fileName = key.split('/').pop()?.replace(/\.tsx?$/, '') ?? '';
    if (!pages[pluginId]) pages[pluginId] = {};
    // Don't overwrite a pages/ entry with a components/ entry
    if (!pages[pluginId][fileName]) {
      pages[pluginId][fileName] = loader;
    }
  }

  return pages;
}

const moduleLookup = buildModuleLookup();
const pageLookup = buildPageLookup();

type PluginPermissionMetadata = {
  pluginRequired: string[];
  featurePermissionsByName: Record<string, string[]>;
  uiRoutePermissions: Array<{ path: string; requiredPermissions: string[] }>;
};

function uniquePermissions(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)));
}

function normalizePath(pathValue: string): string {
  const trimmed = pathValue.trim();
  if (!trimmed) return '/';
  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;
}

function pathPatternToRegex(pattern: string): RegExp {
  const normalized = normalizePath(pattern);
  const regex = normalized
    .split('/')
    .map(segment => {
      if (segment === '*') return '.*';
      if (segment.startsWith(':')) return '[^/]+';
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return new RegExp(`^${regex}$`);
}

function buildPluginPermissionMetadata(pluginId: string, manifest: Record<string, any> | null | undefined): PluginPermissionMetadata {
  const permissionBlock = manifest?.permissions && typeof manifest.permissions === 'object'
    ? manifest.permissions
    : null;
  const pluginRequired = Array.isArray(permissionBlock?.required)
    ? uniquePermissions(permissionBlock.required)
    : [`plugin.${pluginId}.view`];

  const featurePermissionsByName = permissionBlock?.features && typeof permissionBlock.features === 'object'
    ? Object.fromEntries(
        Object.entries(permissionBlock.features).map(([feature, permissions]) => [
          feature,
          Array.isArray(permissions) ? uniquePermissions(permissions as string[]) : [],
        ]),
      )
    : {};

  const uiRoutePermissions = Array.isArray(manifest?.ui?.routes)
    ? manifest.ui.routes
        .filter((route: any) => route && typeof route.path === 'string')
        .map((route: any) => ({
          path: route.path,
          requiredPermissions: Array.isArray(route.requiredPermissions)
            ? uniquePermissions(route.requiredPermissions)
            : [],
        }))
    : [];

  return {
    pluginRequired,
    featurePermissionsByName,
    uiRoutePermissions,
  };
}

function inferFeaturePermissions(pathValue: string, metadata: PluginPermissionMetadata): string[] {
  const normalizedPath = normalizePath(pathValue).toLowerCase();
  const inferred: string[] = [];
  for (const [feature, permissions] of Object.entries(metadata.featurePermissionsByName)) {
    if (!permissions.length) continue;
    const featureToken = `/${feature.toLowerCase()}`;
    if (normalizedPath.includes(featureToken)) {
      inferred.push(...permissions);
    }
  }
  return uniquePermissions(inferred);
}

function resolveRoutePermissions(pathValue: string, metadata: PluginPermissionMetadata): string[] {
  const normalizedPath = normalizePath(pathValue);
  const uiRouteRequired = metadata.uiRoutePermissions
    .filter(route => pathPatternToRegex(route.path).test(normalizedPath))
    .flatMap(route => route.requiredPermissions);
  const featureRequired = inferFeaturePermissions(normalizedPath, metadata);
  return uniquePermissions([
    ...metadata.pluginRequired,
    ...uiRouteRequired,
    ...featureRequired,
  ]);
}

function mergeRequiredPermissions(
  declaredPermissions: string[] | undefined,
  pathValue: string,
  metadata: PluginPermissionMetadata,
): string[] {
  return uniquePermissions([
    ...(Array.isArray(declaredPermissions) ? declaredPermissions : []),
    ...resolveRoutePermissions(pathValue, metadata),
  ]);
}

debugLog('[PluginLoader] Module lookup:', Object.keys(moduleLookup));
debugLog('[PluginLoader] Page lookup:', Object.fromEntries(
  Object.entries(pageLookup).map(([k, v]) => [k, Object.keys(v)])
));

// ── Loader logic ────────────────────────────────────────────────

async function loadPluginUI(pluginId: string, manifestCapabilities?: Record<string, unknown> | null, manifest?: Record<string, any> | null) {
  debugLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
  debugLog(`[PluginLoader] 🔄 Starting UI load for plugin "${pluginId}"`);
  debugLog(`━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`);
  
  const moduleLoader = moduleLookup[pluginId];
  if (!moduleLoader) {
    console.warn(`[PluginLoader] ❌ No UI module found for plugin "${pluginId}"`);
    debugLog(`[PluginLoader] Available plugins in moduleLookup:`, Object.keys(moduleLookup));
    return;
  }

  debugLog(`[PluginLoader] ✅ Found UI module loader for "${pluginId}"`);
  const mod = await moduleLoader();

  // Find integration points export
  const integrationPoints =
    mod.default ??
    mod.integrationPoints ??
    mod[`${pluginId}IntegrationPoints`] ??
    // Convention: <pluginId>IntegrationPoints (camelCase quirks)
    Object.values(mod).find(
      (v: any) => v && typeof v === 'object' && Array.isArray(v.pages),
    );

  if (!integrationPoints) {
    console.warn(`[PluginLoader] No integration points found for "${pluginId}"`);
    return;
  }

  // ── DIAGNOSTIC: Integration Points ──────────────────────────
  debugLog('PLUGIN_UI_MODULE', pluginId, mod);
  debugLog('INTEGRATION_POINTS', pluginId, integrationPoints);
  debugLog(`[PluginLoader] Integration points for "${pluginId}":`, integrationPoints);

  const permissionMetadata = buildPluginPermissionMetadata(pluginId, manifest);

  // ── Register pages ──────────────────────────────────────────
  const pageStore = usePageRegistry.getState();
  const pluginPageMap = pageLookup[pluginId] ?? {};

  // Cache lazy-wrapped components per pluginId:componentName so that multiple
  // page registrations sharing the same underlying component (e.g. /people and
  // /people/:id both mapping to PeoplePage) get the SAME React element type.
  // Without this, React treats them as different components on navigation and
  // performs a full unmount/remount — resetting all state and producing the
  // repeated "PluginPage unmounted (teardown)" logs.
  const lazyComponentCache = new Map<string, React.ComponentType<Record<string, unknown>>>();

  debugLog("PLUGIN_PAGE_MAP", pluginId, Object.keys(pluginPageMap));

  if (Array.isArray(integrationPoints.pages)) {
    debugLog(`[PluginLoader] Processing ${integrationPoints.pages.length} page(s) for plugin "${pluginId}"`);
    for (const pageDef of integrationPoints.pages) {
      // Handle string component names and direct React component references
      if (typeof pageDef.component === 'function') {
        const compFn = pageDef.component as Function;
        // React component reference — wrap directly
        const DirectComp = compFn as React.ComponentType<any>;
        const component: React.ComponentType<Record<string, unknown>> = (props: Record<string, unknown>) => (
          <PluginErrorBoundary pluginId={pluginId}>
            <DirectComp {...props} />
          </PluginErrorBoundary>
        );
        component.displayName = `PluginPage(${pluginId}:${pageDef.path})`;
        const pageRegistration: PluginPageDefinition = {
          id: `${pluginId}:${String(pageDef.path)}`,
          pluginId,
          path: String(pageDef.path),
          title: String(pageDef.title),
          icon: pageDef.icon != null ? String(pageDef.icon) : undefined,
          component,
          showInNav: pageDef.showInNav !== false,
          order: Number(pageDef.order ?? 100),
          requiredPermissions: mergeRequiredPermissions(pageDef.requiredPermissions, String(pageDef.path), permissionMetadata),
        };
        pageStore.registerPage(pageRegistration);
        debugLog(`[PluginLoader] \u2705 Registered page (component) \"${pageDef.path}\" for \"${pluginId}\"`);
        continue;
      }

      const componentName: string = pageDef.component;
      const pagePath: string = pageDef.path ?? pageDef.id ?? '';
      debugLog(`[PluginLoader] Looking for page component "${componentName}" for path "${pagePath}"`);
      debugLog(`[PluginLoader] Available components for "${pluginId}":`, Object.keys(pluginPageMap));
      const pageLoader = pluginPageMap[componentName];

      if (!pageLoader) {
        console.error(
          `[PluginLoader] ❌ Page component "${componentName}" not found for plugin "${pluginId}". ` +
          `Available: ${Object.keys(pluginPageMap).join(', ')}`,
        );
        continue;
      }

      if (!pagePath) {
        console.error(
          `[PluginLoader] ❌ Page definition for "${componentName}" in plugin "${pluginId}" has no path or id.`,
        );
        continue;
      }

      // Reuse the same component wrapper when multiple pages share the same
      // underlying component name (e.g. /people and /people/:id → PeoplePage).
      // This ensures React sees the SAME component type on navigation between
      // those pages, turning the transition into a prop update instead of a
      // full unmount/remount cycle.
      const cacheKey = `${pluginId}:${componentName}`;
      let component = lazyComponentCache.get(cacheKey);

      if (!component) {
        const LazyComp = lazy(() =>
          pageLoader().then((m: any) => ({
            default: m[componentName] ?? m.default,
          })),
        );

        // Wrap with error boundary to prevent plugin UI crashes from breaking host
        component = (props: Record<string, unknown>) => (
          <PluginErrorBoundary pluginId={pluginId}>
            <LazyComp {...props} />
          </PluginErrorBoundary>
        );
        component.displayName = `PluginPage(${pluginId}:${componentName})`;
        lazyComponentCache.set(cacheKey, component);
      } else {
        debugLog(`[PluginLoader] ♻️ Reusing cached component wrapper "${componentName}" for "${pagePath}"`);
      }

      const pageRegistration: PluginPageDefinition = {
        id: `${pluginId}:${pagePath}`,
        pluginId,
        path: pagePath,
        title: String(pageDef.title),
        icon: pageDef.icon != null ? String(pageDef.icon) : undefined,
        component,
        showInNav: pageDef.showInNav !== false,
        order: Number(pageDef.order ?? 100),
        requiredPermissions: mergeRequiredPermissions(pageDef.requiredPermissions, pagePath, permissionMetadata),
      };
      
      debugLog(`[PluginLoader] Registering page with data:`, pageRegistration);
      pageStore.registerPage(pageRegistration);

      debugLog(`[PluginLoader] ✅ Successfully registered page "${pagePath}" → component "${componentName}"`);
      
      // Verify registration worked
      const registered = pageStore.getPageByPath(pagePath);
      if (registered) {
        debugLog(`[PluginLoader] ✅ Verification: Page "${pagePath}" is now in registry`);
      } else {
        console.error(`[PluginLoader] ❌ Verification FAILED: Page "${pagePath}" not found in registry after registration!`);
      }
    }
  }

  // ── DIAGNOSTIC: All Registered Pages ──────────────────────────
  debugLog('ALL_REGISTERED_PAGES', Object.fromEntries(usePageRegistry.getState().pages));

  // ── Register navigation items ───────────────────────────────
  // Wave A9.14: Manifest is the canonical source of truth for nav metadata.
  // Register manifest navigation items FIRST — they take priority.
  // IntegrationPoints navigation is a fallback for paths not in the manifest.
  const navStore = useNavigationRegistry.getState();
  const manifestNavPaths = new Set<string>();

  if (manifest && Array.isArray(manifest.navigation)) {
    for (const navDef of manifest.navigation) {
      const visibility = navDef.navVisibility ?? 'top';
      const navItem: NavigationItem = {
        id: `${pluginId}:${navDef.id ?? navDef.label?.toLowerCase()}`,
        pluginId,
        label: navDef.label,
        path: navDef.path,
        icon: navDef.icon,
        order: navDef.order ?? 50,
        group: navDef.group ?? 'main',
        category: navDef.category,
        requiredPermissions: mergeRequiredPermissions(navDef.requiredPermissions, navDef.path, permissionMetadata),
        registeredAt: new Date().toISOString(),
      };
      
      if (visibility === 'sidebar') {
        // Route sidebar items to the sidebar registry
        const sidebarStore = useSidebarRegistry.getState();
        const sectionId = `${pluginId}:nav`;
        sidebarStore.registerSection({
          id: sectionId,
          pluginId,
          title: navDef.label,
          icon: navDef.icon,
          order: navDef.order ?? 50,
          collapsible: true,
          defaultCollapsed: false,
        });
        sidebarStore.registerItem({
          id: `${pluginId}:${navDef.id ?? navDef.label?.toLowerCase()}`,
          sectionId,
          pluginId,
          label: navDef.label,
          icon: navDef.icon,
          path: navDef.path,
          order: navDef.order ?? 50,
        });
        debugLog(`[PluginLoader] ✓ Registered sidebar nav "${navDef.label}" → ${navDef.path}`);
      } else if (visibility === 'hidden' || visibility === 'devtools') {
        debugLog(`[PluginLoader] ⏭ Skipping nav "${navDef.label}" → ${navDef.path} (visibility: ${visibility})`);
        manifestNavPaths.add(navDef.path); // Still track to prevent IP override
      } else {
        // Default: top nav (visibility === 'top' or unset)
        navStore.registerPluginNavItem(navItem);
        manifestNavPaths.add(navDef.path);
        debugLog(`[PluginLoader] ✓ Registered nav item "${navDef.label}" → ${navDef.path}`);
        debugLog('REGISTERED_NAV_ITEM', pluginId, navItem);
      }
    }
  }

  // Register IntegrationPoints navigation items only for paths NOT already
  // covered by the manifest. This ensures manifest metadata always wins.
  if (Array.isArray(integrationPoints.navigation)) {
    for (const navDef of integrationPoints.navigation) {
      // Skip if manifest already declared this path (manifest is canonical)
      if (manifestNavPaths.has(navDef.path)) {
        debugLog(`[PluginLoader] ⏭ Skipping IntegrationPoints nav "${navDef.label}" → ${navDef.path} (manifest is canonical)`);
        continue;
      }
      // IntegrationPoints nav items go to top nav (sidebar should be in manifest)
      const navItem: NavigationItem = {
        id: `${pluginId}:${navDef.id ?? navDef.label?.toLowerCase()}`,
        pluginId,
        label: navDef.label,
        path: navDef.path,
        icon: navDef.icon,
        order: navDef.order ?? 50,
        group: navDef.group ?? 'main',
        category: navDef.category,
        requiredPermissions: mergeRequiredPermissions(navDef.requiredPermissions, navDef.path, permissionMetadata),
        registeredAt: new Date().toISOString(),
      };
      navStore.registerPluginNavItem(navItem);
      debugLog(`[PluginLoader] ✓ Registered IntegrationPoints nav item "${navDef.label}" → ${navDef.path} (fallback)`);
    }
  }

  // ── Register navigation categories from integration points ──
  if (Array.isArray(integrationPoints.navigationCategories)) {
    for (const catDef of integrationPoints.navigationCategories) {
      const category: NavigationCategory = {
        id: `${pluginId}:${catDef.id ?? catDef.title?.toLowerCase()}`,
        pluginId,
        title: catDef.title,
        icon: catDef.icon,
        order: catDef.order ?? 50,
        items: (catDef.items ?? []).map((item: any) => ({
          id: `${pluginId}:${item.id ?? item.label?.toLowerCase()}`,
          pluginId,
          label: item.label,
          path: item.path,
          icon: item.icon,
          order: item.order ?? 0,
          requiredPermissions: mergeRequiredPermissions(item.requiredPermissions, item.path, permissionMetadata),
          registeredAt: new Date().toISOString(),
          children: Array.isArray(item.children)
            ? item.children.map((child: any) => ({
                id: `${pluginId}:${child.id ?? child.label?.toLowerCase()}`,
                pluginId,
                label: child.label,
                path: child.path,
                icon: child.icon,
                order: child.order ?? 0,
                requiredPermissions: mergeRequiredPermissions(child.requiredPermissions, child.path, permissionMetadata),
                registeredAt: new Date().toISOString(),
              }))
            : undefined,
        })),
      };
      navStore.registerPluginCategory(category);
      debugLog(`[PluginLoader] ✓ Registered nav category "${catDef.title}"`);
    }
  }

  // ── DIAGNOSTIC: Final Nav State After Plugin Load ─────────────
  debugLog('FINAL_NAV_ITEMS', useNavigationRegistry.getState().items);
  debugLog('FINAL_NAV_CATEGORIES', useNavigationRegistry.getState().categories);

  // ── Register slot contributions from integration points ─────
  if (Array.isArray(integrationPoints.slotContributions)) {
    for (const slotEntry of integrationPoints.slotContributions) {
      const contribution: SlotContribution = {
        contributionId: slotEntry.contributionId || `${pluginId}:${slotEntry.slotId}`,
        slotId: slotEntry.slotId,
        pluginId,
        componentPath: '',
        component: slotEntry.component,
        priority: slotEntry.priority ?? 50,
        when: slotEntry.when,
      };
      slotRegistry.registerContribution(contribution);
      debugLog(
        `[PluginLoader] ✓ Registered slot contribution from UI module: ` +
        `"${contribution.contributionId}" → slot "${contribution.slotId}"`,
      );
    }
  }

  // ── Register dashboard widgets from integration points ──────
  if (Array.isArray(integrationPoints.dashboardWidgets)) {
    const dashboardStore = useDashboardRegistry.getState();
    const pluginComponentMap = pageLookup[pluginId] ?? {};

    for (const widgetDef of integrationPoints.dashboardWidgets) {
      const componentName: string | undefined = widgetDef.component;
      const panelId = `${pluginId}:${widgetDef.id}`;

      if (!componentName) {
        debugLog(
          `[PluginLoader] ⏭ Dashboard widget "${panelId}" has no component field — skipping`,
        );
        continue;
      }

      // File name, File#Export, or a named export of ui/index.ts.
      const componentLoader = resolveComponentLoader(componentName, pluginComponentMap, mod);

      if (!componentLoader) {
        console.warn(
          `[PluginLoader] Dashboard widget component "${componentName}" not found for plugin "${pluginId}". ` +
          `Available: ${Object.keys(pluginComponentMap).join(', ')}`,
        );
        continue;
      }

      // Lazy-load the widget component and register it with the dashboard registry
      const LazyWidget = lazy(() => componentLoader().then((component) => ({ default: component })));

      // Create a wrapper component that handles the dashboard panel contract:
      // The dashboard passes { config, pluginId } but widgets may expect domain-specific props.
      // The wrapper renders the lazy component inside a Suspense + ErrorBoundary.
      const CapturedLazy = LazyWidget;
      // Rendered with the owning plugin's context (not the dashboard's api client).
      const WidgetWrapper = (props: Record<string, unknown>) => (
        <PluginErrorBoundary pluginId={pluginId}>
          <DashboardPluginWidget pluginId={pluginId} panelId={panelId} Component={CapturedLazy} dashboardProps={props} />
        </PluginErrorBoundary>
      );
      WidgetWrapper.displayName = `DashboardWidget(${pluginId}:${componentName})`;

      dashboardStore.registerComponent(panelId, WidgetWrapper);

      if (typeof globalThis !== 'undefined') {
        const sharedRegistry = ((globalThis as typeof globalThis & {
          __NEURO_DASHBOARD_PANEL_REGISTRY__?: {
            components: Map<string, React.ComponentType<any>>;
            definitions: Map<string, {
              id: string;
              plugin: string;
              component?: React.ComponentType<any>;
              slots: string[];
              permissions?: string[];
            }>;
          };
        }).__NEURO_DASHBOARD_PANEL_REGISTRY__ ??= {
          components: new Map<string, React.ComponentType<any>>(),
          definitions: new Map(),
        });

        const widgetSlots = Array.isArray((widgetDef as { slots?: string[] }).slots)
          ? ((widgetDef as { slots?: string[] }).slots ?? []).filter(Boolean)
          : ['center'];

        sharedRegistry.components.set(panelId, WidgetWrapper);
        sharedRegistry.definitions.set(panelId, {
          id: panelId,
          plugin: pluginId,
          component: WidgetWrapper,
          slots: widgetSlots.length > 0 ? widgetSlots : ['center'],
          permissions: widgetDef.requiredPermissions,
        });
      }

      debugLog(`[PluginLoader] ✓ Registered dashboard widget "${panelId}" → ${componentName}`);
    }
  }

  // ── Register action descriptors from plugin UI module ───────
  const actionStore = useActionRegistryStore.getState();

  if (Array.isArray(integrationPoints.actions)) {
    for (const actionDef of integrationPoints.actions) {
      // Normalize: some plugins use `id` instead of `name`
      const actionName = actionDef.name ?? (actionDef as any).id ?? actionDef.label;
      actionStore.registerAction({
        id: actionDef.id ?? (actionDef as any).id ?? `${pluginId}.${actionName}`,
        pluginId,
        name: actionName,
        label: actionDef.label,
        description: actionDef.description ?? '',
        icon: actionDef.icon,
        category: actionDef.category ?? 'utility',
        accepts: actionDef.accepts ?? [],
        produces: actionDef.produces ?? 'text.plain',
        uiMode: actionDef.uiMode,
        interaction: actionDef.interaction,
        uiComponent: actionDef.uiComponent,
        shortcut: actionDef.shortcut,
        priority: actionDef.priority,
        entityType: actionDef.entityType,
        requiredPermission: actionDef.requiredPermission,
        timeout: actionDef.timeout,
      });
      debugLog(`[PluginLoader] ✓ Registered action "${actionName}" from UI module`);
    }
  }

  // Register action UI components if provided
  if (integrationPoints.actionComponents && typeof integrationPoints.actionComponents === 'object') {
    for (const [name, component] of Object.entries(integrationPoints.actionComponents)) {
      if (typeof component === 'function') {
        actionStore.registerUIComponent(name, component as React.ComponentType<any>);
        debugLog(`[PluginLoader] ✓ Registered action UI component "${name}"`);
      }
    }
  }

  // ── Register entity rendering info with EntityTypeRegistry ────
  const entityRendering = integrationPoints.entityRendering;
  if (entityRendering && typeof entityRendering === 'object') {
    const entityTypeStore = useEntityTypeRegistry.getState();
    const registrations = Array.isArray(entityRendering) ? entityRendering : [entityRendering];

    for (const er of registrations) {
      if (er.type) {
        entityTypeStore.registerEntityType({
          entityType: er.type,
          icon: er.icon ?? '📄',
          label: er.label ?? er.pluralLabel ?? er.type,
          route: er.defaultRoute,
          color: er.color,
          pluginId,
          order: er.order,
          mobileIcon: er.mobileIcon,
          routeBuilder: er.routeBuilder,
        });
        debugLog(`[PluginLoader] ✓ Registered entity type "${er.type}" from plugin "${pluginId}"`);
      }
    }
  }

  // ── Register page actions from integration points ───────────
  const pageActionStore = usePageActionRegistry.getState();

  if (Array.isArray(integrationPoints.pageActions)) {
    for (const actionDef of integrationPoints.pageActions) {
      // Synthesize onExecute from declarative action/event/handler properties
      // if the plugin didn't provide an explicit onExecute handler.
      let onExecute = actionDef.onExecute;
      if (typeof onExecute !== 'function') {
        const declAction = (actionDef as any).action;
        const declEvent = (actionDef as any).event;
        const declHandler = (actionDef as any).handler;
        if (typeof declHandler === 'function') {
          onExecute = declHandler;
          debugLog(`[PluginLoader] Synthesized onExecute (handler fn) for page action "${actionDef.id}"`);
        } else if (declAction && declAction.type === 'navigate' && declAction.path) {
          const navPath = declAction.path;
          onExecute = () => { window.location.assign(navPath); };
          debugLog(`[PluginLoader] Synthesized onExecute (navigate → ${navPath}) for page action "${actionDef.id}"`);
        } else if (declEvent) {
          const eventName = declEvent;
          onExecute = () => { window.dispatchEvent(new CustomEvent(eventName)); };
          debugLog(`[PluginLoader] Synthesized onExecute (event → ${eventName}) for page action "${actionDef.id}"`);
        } else {
          onExecute = () => { console.warn(`[PageAction] No handler for action "${actionDef.id}" from plugin "${pluginId}"`); };
        }
      }
      pageActionStore.registerAction({
        ...actionDef,
        id: actionDef.id ?? `${pluginId}.${actionDef.label?.toLowerCase().replace(/\s+/g, '-')}`,
        pluginId,
        onExecute,
      });
      debugLog(`[PluginLoader] ✓ Registered page action "${actionDef.id ?? actionDef.label}" from plugin "${pluginId}"`);
    }
  }

  // ── Register overlay commands from integration points ───────
  if (Array.isArray(integrationPoints.overlayCommands)) {
    const overlayCommandStore = useOverlayCommandRegistry.getState();
    for (const cmdDef of integrationPoints.overlayCommands) {
      if (!cmdDef || !cmdDef.id) continue;
      overlayCommandStore.registerCommand(cmdDef);
      debugLog(`[PluginLoader] ✓ Registered overlay command "${cmdDef.id}" from plugin "${pluginId}"`);
    }
  }

  // ── Register entity type UI from integration points ─────────
  // Accept both array and object forms of entityTypeUI
  const rawEntityTypeUI = integrationPoints.entityTypeUI;
  const entityTypeUIArr: any[] = Array.isArray(rawEntityTypeUI)
    ? rawEntityTypeUI
    : rawEntityTypeUI && typeof rawEntityTypeUI === 'object'
      ? Object.values(rawEntityTypeUI)
      : [];

  if (entityTypeUIArr.length > 0) {
    const entityTypeStore = useEntityTypeRegistry.getState();
    for (const def of entityTypeUIArr) {
      if (!def || !def.entityType) continue;
      // Normalize: accept displayName as fallback for label (common plugin pattern)
      const label = def.label ?? (def as any).displayName ?? def.entityType;
      entityTypeStore.registerEntityType({
        entityType: def.entityType,
        icon: def.icon,
        label,
        color: def.color,
        pluginId,
        route: def.staticRoute,
        mobileIcon: def.mobileIcon,
        routeBuilder: def.route,
      });
      debugLog(`[PluginLoader] ✓ Registered entity type UI "${def.entityType}" from plugin "${pluginId}"`);
    }
  }

  // ── Register search result types from integration points ──────
  const rawSearchResultTypes = integrationPoints.searchResultTypes;
  const searchResultTypesArr: any[] = Array.isArray(rawSearchResultTypes)
    ? rawSearchResultTypes
    : rawSearchResultTypes && typeof rawSearchResultTypes === 'object'
      ? Object.values(rawSearchResultTypes)
      : [];

  if (searchResultTypesArr.length > 0) {
    const searchResultTypeStore = useSearchResultTypeRegistry.getState();
    for (const def of searchResultTypesArr) {
      if (!def || !def.type) continue;
      searchResultTypeStore.registerSearchResultType({
        ...def,
        pluginId,
      });
      debugLog(`[PluginLoader] ✓ Registered search result type "${def.type}" from plugin "${pluginId}"`);
    }
  }

  // ── Register capabilities for ALL plugins (not just those with setup()) ──
  // This ensures capability checks in PluginPageHost always find entries,
  // even for plugins that only declare pages/navigation without a setup().
  const uiExtensions = createPluginUIExtensions(pluginId, {
    isMobile: typeof window !== 'undefined' && window.innerWidth <= 768,
    deviceType: typeof window !== 'undefined'
      ? window.innerWidth <= 768 ? 'mobile'
        : window.innerWidth <= 1024 ? 'tablet'
        : 'desktop'
      : 'desktop',
  }, manifestCapabilities as any ?? undefined);

  // ── Call plugin setup function with UI extensions if available ─
  if (typeof integrationPoints.setup === 'function') {
    try {

      // Wave 22.13.1 / 22.13.5: Track injected APIs on the extensions object for DevTools
      (uiExtensions as any).__injectedAPIs = {
        ui: !!uiExtensions.ui,
        actions: !!uiExtensions.actions,
        events: !!uiExtensions.events,
        contentSync: !!uiExtensions.contentSync,
      };
      debugLog(`[PluginLoader] ✓ Injected interaction APIs (ui/actions/events/contentSync) for plugin "${pluginId}"`);

      integrationPoints.setup(uiExtensions);
      debugLog(`[PluginLoader] ✓ Called setup() for plugin "${pluginId}"`);

      // ── Phase 11AA: Register crash-recovery teardown handler ──────
      // Bridge plugin-registered onTeardown callbacks to the crash-recovery
      // system so that handlePluginCrash() can clean up plugin resources
      // (event subscriptions, contentSync, modals, etc.) before disabling.
      registerTeardownHandler(pluginId, () => {
        executePluginTeardown(pluginId);
      });

      // ── Wave 22.13.2: Runtime verification of injected API usage ──
      // Verify that the setup function actually received valid API surfaces.
      // If any API surface is missing, record it as a runtime verification failure.
      const verificationResult = {
        pluginId,
        hasSetup: true,
        apisAvailable: {
          ui: typeof uiExtensions.ui?.openModal === 'function',
          actions: typeof uiExtensions.actions?.register === 'function' && typeof uiExtensions.actions?.defer === 'function',
          events: typeof uiExtensions.events?.emit === 'function' && typeof uiExtensions.events?.on === 'function',
          contentSync: typeof uiExtensions.contentSync?.publish === 'function' && typeof uiExtensions.contentSync?.subscribe === 'function',
        },
        registerModalComponentAvailable: typeof uiExtensions.registerModalComponent === 'function',
        timestamp: new Date().toISOString(),
      };
      recordPluginSetupVerification(pluginId, verificationResult);
      debugLog(`[PluginLoader] ✓ Runtime verification for plugin "${pluginId}":`, verificationResult);
    } catch (setupErr) {
      console.error(`[PluginLoader] setup() failed for plugin "${pluginId}":`, setupErr);
      recordPluginSetupVerification(pluginId, {
        pluginId,
        hasSetup: true,
        apisAvailable: { ui: false, actions: false, events: false, contentSync: false },
        registerModalComponentAvailable: false,
        setupError: String(setupErr),
        timestamp: new Date().toISOString(),
      });
    }
  } else {
    // ── Wave 22.13.2: Detect plugins with action descriptors but no setup() ──
    // If a plugin declares interactive actions (interaction: 'modal') but has no
    // setup() function, the actions will never be wired to the runtime APIs.
    const hasInteractiveActions = Array.isArray(integrationPoints.actions) &&
      integrationPoints.actions.some((a: any) => a.interaction === 'modal' || a.uiMode === 'replace');

    if (hasInteractiveActions) {
      console.warn(
        `[PluginLoader] ⚠ Plugin "${pluginId}" declares interactive actions but has no setup() function. ` +
        `Actions requiring modals will not function at runtime. Add a setup(context) function to wire interactive-actions.ts.`
      );
      recordPluginSetupVerification(pluginId, {
        pluginId,
        hasSetup: false,
        apisAvailable: { ui: false, actions: false, events: false, contentSync: false },
        registerModalComponentAvailable: false,
        missingSetupWithInteractiveActions: true,
        timestamp: new Date().toISOString(),
      });
    }
  }

  // ── Wave 22.13.1: Register modal components from integration points ─
  if (integrationPoints.modalComponents && typeof integrationPoints.modalComponents === 'object') {
    for (const [viewId, component] of Object.entries(integrationPoints.modalComponents)) {
      if (typeof component === 'function') {
        registerModalComponent(viewId, component as React.ComponentType<any>);
        debugLog(`[PluginLoader] ✓ Registered modal component "${viewId}" from plugin "${pluginId}"`);
      }
    }
  }

  // ── Embeddable components (manifest uiComponents + dashboard widgets + pages) ─
  registerPluginEmbeddables({
    pluginId,
    manifest,
    integrationPoints,
    uiModule: mod,
    fileLoaders: pageLookup[pluginId] ?? {},
    resolvePermissions: (declared, pathValue) => mergeRequiredPermissions(declared, pathValue, permissionMetadata),
  });
}

// ── Manifest-driven slot registration ────────────────────────────

/**
 * Process a plugin manifest's `slots` field:
 * - Register slot definitions (plugin-scoped extension points)
 * - Register slot contributions (components targeting other slots)
 * - Resolve contribution components via Vite glob
 */
function registerManifestSlots(pluginId: string, manifest: any): void {
  if (!manifest?.slots) return;

  const { defines, contributes } = manifest.slots;

  // Register slot definitions
  if (Array.isArray(defines)) {
    for (const def of defines as ManifestSlotDefinition[]) {
      slotRegistry.defineSlot({
        id: def.id,
        description: def.description,
        multiple: def.multiple ?? true,
        ownerPluginId: pluginId,
      });
      debugLog(`[PluginLoader] ✓ Registered slot definition: "${def.id}" (owner: ${pluginId})`);
    }
  }

  // Register slot contributions from manifest
  if (Array.isArray(contributes)) {
    for (const contrib of contributes as ManifestSlotContribution[]) {
      // Resolve the component from Vite glob
      const componentName = contrib.component
        .replace(/^\.\/ui\/components\//, '')
        .replace(/^\.\/ui\//, '')
        .replace(/\.tsx?$/, '');

      const componentLoader = findComponentLoader(pluginId, componentName);

      const contribution: SlotContribution = {
        contributionId: `${pluginId}:${contrib.slotId}:${componentName}`,
        slotId: contrib.slotId,
        pluginId,
        componentPath: contrib.component,
        priority: contrib.priority ?? 50,
        when: contrib.when,
      };

      if (componentLoader) {
        // Lazy-load the component
        const LazyComponent = lazy(() =>
          componentLoader().then((m: any) => ({
            default: m[componentName] ?? m.default,
          })),
        );
        contribution.component = LazyComponent;
      }

      slotRegistry.registerContribution(contribution);
      debugLog(
        `[PluginLoader] ✓ Registered manifest slot contribution: ` +
        `"${contribution.contributionId}" → slot "${contrib.slotId}"` +
        (componentLoader ? '' : ' (component not yet resolved)'),
      );
    }
  }
}

/**
 * Find a component loader from the Vite glob maps.
 */
function findComponentLoader(
  pluginId: string,
  componentName: string,
): (() => Promise<any>) | undefined {
  const pluginPageMap = pageLookup[pluginId] ?? {};
  return pluginPageMap[componentName];
}

// ── Bridge connector ────────────────────────────────────────────

/**
 * Invisible component that wires the plugin toast & modal bridges
 * to the real notification system. Must be rendered inside
 * NotificationProvider (which is guaranteed since PluginLoader
 * is rendered inside App, which is inside NotificationProvider).
 */
function PluginBridgeConnector() {
  const { showSuccess, showError, showWarning, showInfo } = useNotifications();
  const [modalRequest, setModalRequest] = useState<PluginModalRequest | null>(null);

  useEffect(() => {
    actionResultBridge.connect(({ level, title, message }) => {
      if (level === 'success') showSuccess(title, message);
      else if (level === 'error') showError(title, message);
      else if (level === 'warning') showWarning(title, message);
      else showInfo(title, message);
    });

    pluginToastBridge.connect({
      success: (msg) => actionResultBridge.success('Plugin action succeeded', msg, 'plugin'),
      error: (msg) => actionResultBridge.error('Plugin action failed', msg, 'plugin'),
      warning: (msg) => actionResultBridge.warning('Plugin warning', msg, 'plugin'),
      info: (msg) => actionResultBridge.info('Plugin info', msg, 'plugin'),
    });
    return () => {
      pluginToastBridge.disconnect();
      actionResultBridge.disconnect();
    };
  }, [showSuccess, showError, showWarning, showInfo]);

  useEffect(() => {
    pluginModalBridge.connect({
      onOpen: (request) => setModalRequest(request),
      onClose: () => setModalRequest(null),
    });
    return () => pluginModalBridge.disconnect();
  }, []);

  // Render plugin modal if requested
  // For now, render a simple overlay. Plugin authors can register
  // custom modal components in a future wave.
  if (modalRequest) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Plugin modal: ${modalRequest.modalId}`}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(0,0,0,0.5)',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            pluginModalBridge.close();
          }
        }}
      >
        <div
          style={{
            background: 'var(--nc-color-surface, #1a1a2e)',
            borderRadius: 'var(--nc-radius-lg, 12px)',
            padding: 'var(--nc-space-xl, 24px)',
            minWidth: 320,
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--nc-space-md, 16px)' }}>
            <h3 style={{ margin: 0, color: 'var(--nc-color-text, #fff)' }}>
              {modalRequest.modalId}
            </h3>
            <button
              onClick={() => pluginModalBridge.close()}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--nc-color-text-muted, #888)',
                cursor: 'pointer',
                fontSize: '1.25rem',
                padding: '0.25rem',
              }}
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
          {modalRequest.props && (
            <pre style={{ color: 'var(--nc-color-text-muted, #888)', fontSize: '0.8rem', overflow: 'auto' }}>
              {JSON.stringify(modalRequest.props, null, 2)}
            </pre>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// ── React component ──────────────────────────────────────────────

/**
 * PluginLoader wraps the application and ensures plugin UI modules
 * are loaded and registered BEFORE the router renders.
 *
 * On first mount it:
 * 1. Fetches the enabled plugin list from the backend.
 * 2. Loads each plugin's UI module and registers pages + navigation.
 * 3. Refreshes navigation from the backend (merge order: Core → Plugin → API).
 * 4. Only THEN renders children (the router tree).
 *
 * Hot-Reload Support (Phase 3.1):
 * When a plugin.hotReloaded event is received, the loader:
 * 1. Unregisters all UI registrations for that plugin
 * 2. Re-imports and re-registers the plugin's UI module
 * 3. Increments loadGeneration to force React to re-render
 *
 * This prevents the race where the router renders before plugin
 * pages/nav are registered, causing 404s on plugin routes.
 */
export function PluginLoader({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Phase 3.1: Replace didRun ref with loadGeneration state so hot-reload
  // can trigger re-loading by incrementing this counter.
  const [loadGeneration, setLoadGeneration] = useState(0);
  const initialLoadComplete = useRef(false);

  // Phase 3.1: Subscribe to hot-reload events for single-plugin reload
  useEffect(() => {
    // Only set up hot-reload listener after initial load completes
    if (!initialLoadComplete.current) return;

    // Dynamic import to avoid bundling socket.io-client in production
    let unsubscribe: (() => void) | null = null;

    const setupHotReloadListener = async () => {
      try {
        const { pluginHotReloadClient } = await import('./hot-reload/PluginFrontendHotReload');
        
        unsubscribe = pluginHotReloadClient.addListener(async (message) => {
          if (message.type === 'plugin.hotReloaded' && message.pluginId) {
            const pluginId = message.pluginId;
            debugLog(`[PluginLoader] 🔄 Hot-reload received for "${pluginId}" — re-loading UI`);

            try {
              // Phase 3.4: Clean all registries for this plugin before reload
              cleanupPluginRegistrations(pluginId);

              // Re-fetch manifest and reload UI module
              const manifestKey = Object.keys(pluginManifests).find(
                (k) => extractPluginId(k) === pluginId,
              );
              const manifest = manifestKey 
                ? ((pluginManifests[manifestKey] as any)?.default ?? pluginManifests[manifestKey])
                : null;

              // Re-register with slot registry (pass manifest dependencies)
              const hotReloadDeps: Record<string, string> =
                manifest?.dependencies && typeof manifest.dependencies === 'object'
                  ? manifest.dependencies
                  : {};
              slotRegistry.registerPlugin(pluginId, manifest?.version ?? '1.0.0', hotReloadDeps);

              if (manifest) {
                registerManifestSlots(pluginId, manifest);
                const capDecl = manifest?.capabilities;
                const isCapabilityDeclaration = capDecl && typeof capDecl === 'object' && !Array.isArray(capDecl);
                await loadPluginUI(pluginId, isCapabilityDeclaration ? capDecl : null, manifest);
              } else {
                await loadPluginUI(pluginId, null, null);
              }

              // Increment generation to force re-render of any components
              // that depend on plugin registrations
              setLoadGeneration(gen => gen + 1);

              debugLog(`[PluginLoader] ✅ Hot-reload complete for "${pluginId}"`);
            } catch (reloadErr) {
              console.error(`[PluginLoader] Hot-reload failed for "${pluginId}":`, reloadErr);
              handlePluginCrash(pluginId, reloadErr instanceof Error ? reloadErr : new Error(String(reloadErr)));
            }
          }
        });
      } catch (err) {
        // Hot-reload client not available (production build)
        debugLog('[PluginLoader] Hot-reload listener not available:', err);
      }
    };

    setupHotReloadListener();

    return () => {
      unsubscribe?.();
    };
  }, [initialLoadComplete.current]);

  // ── Global navigation refresh handler ────────────────────────
  // Plugins can trigger a full navigation refresh by dispatching:
  //   window.dispatchEvent(new CustomEvent('neuro:navigation:refresh'))
  //
  // This is used by plugins that manage dynamic nav entries (e.g. skill-pages
  // creating new skill-page configs that should appear in the sidebar).
  useEffect(() => {
    const handleNavRefresh = async () => {
      debugLog('[PluginLoader] 🔄 Received neuro:navigation:refresh event — refreshing nav registry');
      try {
        await useNavigationRegistry.getState().refresh();
        debugLog('[PluginLoader] ✅ Navigation refresh complete (event-triggered)');
      } catch (err) {
        console.warn('[PluginLoader] Navigation refresh failed:', err);
      }
    };

    window.addEventListener('neuro:navigation:refresh', handleNavRefresh);
    return () => window.removeEventListener('neuro:navigation:refresh', handleNavRefresh);
  }, []);

  useEffect(() => {
    // Phase 3.1: Only run initial load once (loadGeneration is ignored for this effect)
    if (initialLoadComplete.current) return;
    initialLoadComplete.current = true;

    (async () => {
      // ── DIAGNOSTIC: Loader Timing ─────────────────────────────
      debugLog('PLUGIN_LOADER_START');
      const loaderStartTime = performance.now();
      try {
        // ── Fetch enabled plugins from backend ──────────────────────────
        let pluginIds: string[] = [];

        const response = await listPlugins();
        const plugins = response?.plugins ?? [];
        const enabled = plugins.filter((p: any) => p.enabled !== false);
        pluginIds = enabled.map((p: any) => p.pluginId ?? p.id);
        debugLog(`[PluginLoader] ${enabled.length} enabled plugins of ${plugins.length} total from backend`);

        debugLog('[PluginLoader] Plugin IDs to load:', pluginIds);

        // ── Register platform core overlay commands ──────────────────
        registerCoreCommands();
        debugLog('[PluginLoader] ✓ Core overlay commands registered');

        for (const pluginId of pluginIds) {
          try {
            // Wave 22.13.9: Skip plugins disabled by crash recovery
            if (isPluginDisabledByCrash(pluginId)) {
              console.warn(`[PluginLoader] ⚠ Skipping plugin "${pluginId}" — disabled by crash recovery`);
              usePluginLogStore.getState().appendLog(pluginId, 'warn', 'Skipped during load — disabled by crash recovery', 'PluginLoader');
              continue;
            }

            // Register with slot registry (pass manifest dependencies so
            // cross-plugin slot contributions pass dependency validation)
            const manifestKeyForDeps = Object.keys(pluginManifests).find(
              (k) => extractPluginId(k) === pluginId,
            );
            const manifestForDeps = manifestKeyForDeps
              ? ((pluginManifests[manifestKeyForDeps] as any)?.default ?? pluginManifests[manifestKeyForDeps])
              : null;
            const manifestDeps: Record<string, string> =
              manifestForDeps?.dependencies && typeof manifestForDeps.dependencies === 'object'
                ? manifestForDeps.dependencies
                : {};
            const manifestVersion: string = manifestForDeps?.version ?? '1.0.0';
            slotRegistry.registerPlugin(pluginId, manifestVersion, manifestDeps);

            // Process manifest if available
            const manifestKey = manifestKeyForDeps;
            const manifest = manifestForDeps;
            if (manifestKey && manifest) {
              registerManifestSlots(pluginId, manifest);

              // Extract capability declaration for enforcement
              const capDecl = manifest?.capabilities;
              const isCapabilityDeclaration = capDecl && typeof capDecl === 'object' && !Array.isArray(capDecl);
              // Pass manifest to loadPluginUI so it can register
              // manifest navigation as the canonical source of truth
              await loadPluginUI(pluginId, isCapabilityDeclaration ? capDecl : null, manifest);
            } else {
              await loadPluginUI(pluginId, null, null);
            }
          } catch (err) {
            console.error(`[PluginLoader] Failed to load UI for ${pluginId}:`, err);
            // Wave 22.13.9: Record crash in stability system
            handlePluginCrash(pluginId, err instanceof Error ? err : new Error(String(err)));
          }
        }

        // After loading plugin UI, refresh navigation from backend
        // Merge order: Core defaults → Plugin items → Backend API
        debugLog('[PluginLoader] Refreshing navigation registry from backend...');
        await useNavigationRegistry.getState().refresh();
        debugLog('[PluginLoader] Navigation refresh complete');

        // Sync action registry from backend (merges with UI-registered actions)
        debugLog('[PluginLoader] Syncing action registry from backend...');
        try {
          const actionsRes = await client.get<{ actions?: unknown[] } | unknown[]>('/api/actions');
          const actionsData = actionsRes.data;
          const backendActions = Array.isArray(actionsData)
            ? actionsData
            : (actionsData as { actions?: unknown[] })?.actions ?? [];
          // Merge: backend actions supplement UI-registered ones (don't overwrite)
          const actionStore = useActionRegistryStore.getState();
          for (const action of backendActions) {
            if (!actionStore.actions.has((action as any).id)) {
              actionStore.registerAction(action as any);
            }
          }
          debugLog(`[PluginLoader] ✓ Synced ${backendActions.length} action(s) from backend`);
        } catch (actionErr) {
          console.warn('[PluginLoader] Action sync failed (non-critical):', actionErr);
        }

        // Hydrate entity-type registry from backend entity-provider endpoint
        // Discovers plugin-registered entity types the frontend hasn't seen yet
        debugLog('[PluginLoader] Hydrating entity-type registry from backend...');
        try {
          await useEntityTypeRegistry.getState().hydrateFromBackend();
          debugLog('[PluginLoader] ✓ Entity-type registry hydration complete');
        } catch (entityErr) {
          console.warn('[PluginLoader] Entity-type hydration failed (non-critical):', entityErr);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Plugin loading failed';
        console.error('[PluginLoader]', msg, err);
        setError(msg);
      } finally {
        // Always mark ready so the app is usable even if plugins fail
        // ── DIAGNOSTIC: Loader Timing ─────────────────────────────
        debugLog('PLUGIN_LOADER_FINISH', `${(performance.now() - loaderStartTime).toFixed(1)}ms`);
        setReady(true);
      }
    })();
  }, []);

  if (error) {
    console.error('[PluginLoader] Non-blocking error:', error);
  }

  // Show a minimal loading state while plugins are being discovered.
  // This is fast (< 200ms typically) and prevents route flicker.
  if (!ready) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--nc-color-background, #111)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="nc-spinner" style={{ width: 32, height: 32, margin: '0 auto 0.75rem' }} />
          <div style={{ color: 'var(--nc-color-text-muted, #888)', fontSize: 'var(--nc-size-sm, 0.875rem)' }}>Loading plugins…</div>
        </div>
      </div>
    );
  }

  // Wrap children with design-system providers:
  // - ThemeProvider injects CSS variables + theme context
  // - ToastProvider provides the toast notification API
  // - ComponentRegistryProvider gives plugins access to shared components
  
  // Load theme preference from localStorage
  const savedTheme = typeof window !== 'undefined' 
    ? (localStorage.getItem('theme-mode') as 'light' | 'dark' | null)
    : null;
  const defaultMode = savedTheme || 'dark';
  
  return (
    <ThemeProvider defaultMode={defaultMode}>
      <ToastProvider position="bottom-right">
        <ComponentRegistryProvider registry={componentRegistry}>
          <DeviceTypeProvider>
            <PluginBridgeConnector />
            {children}
          </DeviceTypeProvider>
        </ComponentRegistryProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}
